import { CHART_TYPES } from '../constants/chartTypes';
import { assertNotNil } from '../../../utils/types';
import {
  IAnalysisInfoFieldsFragment,
  IMeasureControl,
  ISliceNameHint,
  Maybe,
} from '../../../utils/apollo/graphqlSchema';
import { isNil } from 'lodash';
import { IBreakdownOverallStatus } from '../../../utils/interfaces';
import {
  OVERALL_COMPARISON_SLICE_KEY,
  OVERALL_SLICE_KEY,
  SEGMENT_SLICE_KEY,
} from '../constants/utils';
import { ISliceFieldsFragment } from '../interfaces';
import { IconName } from '../../Icon';

export const getIconFromTypeInChartSelector: Record<CHART_TYPES, IconName> = {
  [CHART_TYPES.BAR_CHART]: 'chart-bar-icon',
  [CHART_TYPES.RETENTION_BAR]: 'chart-bar-icon',
  [CHART_TYPES.STACKED_COLUMN_CHART]: 'chart-stacked-column-icon',
  [CHART_TYPES.PERCENT_STACKED_AREA_CHART]: 'chart-percentage-area-icon',
  [CHART_TYPES.FUNNEL_CHART]: 'funnel',
  [CHART_TYPES.RETENTION]: 'retention',
  [CHART_TYPES.PREDICTION]: 'chart-prediction-icon',
  [CHART_TYPES.PATH]: 'path',
  [CHART_TYPES.METRIC_CHART]: 'chart-metric-icon',
  [CHART_TYPES.DATA_TABLE]: 'table',
  [CHART_TYPES.EXPRESSION_TABLE]: 'chart-table-icon',
  [CHART_TYPES.PIE_CHART]: 'chart-pie',
  [CHART_TYPES.GEO_MAP]: 'chart-geo-map',
  [CHART_TYPES.LINE_CHART]: 'chart-line',
  [CHART_TYPES.RETENTION_LINE]: 'chart-line',
  [CHART_TYPES.COMPARATIVE_LINE_CHART]: 'chart-line',
  [CHART_TYPES.COHORT_SAMPLE]: 'cohort-inspect',
  [CHART_TYPES.SIGNIFICANCE]: 'significance',
};
export const getContentFromChartType = (type: CHART_TYPES) => {
  switch (type) {
    case CHART_TYPES.BAR_CHART:
      return 'Bar chart visualization.';
    case CHART_TYPES.STACKED_COLUMN_CHART:
      return 'Stacked Column Chart';
    case CHART_TYPES.PERCENT_STACKED_AREA_CHART:
      return 'Stacked Percentage Area Chart';

    case CHART_TYPES.FUNNEL_CHART:
      return 'Funnel Chart';
    case CHART_TYPES.COMPARATIVE_LINE_CHART:
      return 'Comparative Line Chart';

    case CHART_TYPES.RETENTION:
      return 'Retention';
    case CHART_TYPES.RETENTION_BAR:
      return 'Retention Bar';
    case CHART_TYPES.RETENTION_LINE:
      return 'Retention Line';

    case CHART_TYPES.PREDICTION:
      return 'Prediction Chart';
    case CHART_TYPES.METRIC_CHART:
      return 'Metric visualization.';
    case CHART_TYPES.DATA_TABLE:
      return 'Data Table visualization.';
    default:
      return 'Line chart visualization';
  }
};

// Calculate the string length, using for svg position
export const getTextMetrics = (text: string, font: string) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  assertNotNil(context);
  context.font = font;
  return context.measureText(text);
};

export const getTextWidth = (text: string, font: string) =>
  getTextMetrics(text, font).width;

export const isRightOrCmdMetaClickMouseDownEvent = (mouseDownEvent: any) =>
  mouseDownEvent.buttons === 2 ||
  ((mouseDownEvent.ctrlKey || mouseDownEvent.metaKey) &&
    mouseDownEvent.buttons === 1);

export const generateGroupbyHint = (
  hints: ISliceNameHint[],
  rank?: Maybe<number>,
) => {
  // Workaround for KB-3808. It will become unnecessary when KB-3807 is fixed
  if (isNil(hints)) {
    return '';
  }
  let res = '';
  for (const hint of hints) {
    if (res.length !== 0) {
      res += ', ';
    }
    res += `${hint.name}: ${hint.value}`;
  }

  // rank starts from 1, ignore it if it's 0
  if (rank) {
    res += ` (Rank: ${rank})`;
  }
  return res;
};

// calculateImageOffsetFromBreakdownStatus
// returns the offset based on the breakdown type [no_breakdown, breakdown_not_overall, breakdown_overall]
// use image 0 (blue dot) as first index if
// 1. no breakdown
// 2. breakdown overall
// use first image as first index for breakdown_not_overall
export const calculateImageOffsetFromBreakdownStatus = (
  breakdownType: IBreakdownOverallStatus,
) => {
  switch (breakdownType) {
    case IBreakdownOverallStatus.NoBreakdown:
    case IBreakdownOverallStatus.NoBreakdownCompare:
      return 0;
    case IBreakdownOverallStatus.BreakdownNotOverall:
      return 1;
    case IBreakdownOverallStatus.BreakdownOverallCompare:
    case IBreakdownOverallStatus.BreakdownOverall:
      return 0;
    default:
      return 0;
  }
};

export const isOverallSliceKey = (sliceKey: string) =>
  sliceKey === OVERALL_SLICE_KEY || sliceKey === OVERALL_COMPARISON_SLICE_KEY;

export const isOverallSlice = (slice: Pick<ISliceFieldsFragment, 'key'>) =>
  isOverallSliceKey(slice.key);

export const isNotOverallSlice = (slice: Pick<ISliceFieldsFragment, 'key'>) =>
  !isOverallSlice(slice);

export const isOnlyOverallSliceKeys = (sliceKeys: string[]) =>
  (sliceKeys.length === 1 && isOverallSliceKey(sliceKeys[0])) ||
  (sliceKeys.length === 2 &&
    isOverallSliceKey(sliceKeys[0]) &&
    isOverallSliceKey(sliceKeys[1]));

export const isOnlyOverallSlices = (slices: ISliceFieldsFragment[]) =>
  isOnlyOverallSliceKeys(slices.map((s) => s.key));

export const isSegmentSliceKey = (sliceKey: string) =>
  sliceKey === SEGMENT_SLICE_KEY;

export const isSkipPercentView = (
  query: IAnalysisInfoFieldsFragment['query'] | null,
  measureControls: IMeasureControl[],
) => {
  if (query?.__typename === 'ExpressionQuery') {
    return (
      query.measure?.formula?.includes('/') ||
      // if any measure is not count function, skip percent view
      measureControls.some((control) => !control.isCountFunction)
    );
  }
  return false;
};

export const findXAxisLabelIndex = (
  xAxisKey: string | undefined,
  chartDates: string[],
) => {
  if (isNil(xAxisKey)) {
    return undefined;
  }
  const xAxisLabelIndex = chartDates.indexOf(xAxisKey);
  return xAxisLabelIndex >= 0 ? xAxisLabelIndex : undefined;
};
